import React from 'react';
import classnames from 'classnames';

import "./manning-online-all-products.scss";
import { ManningOnlineStore } from '../../store/ManningOnlineStore';
import { ProductListItem, ReadingListButtonState } from '../productListItem/ProductListItem';
import ValueUtils from '../../../ts/utils/ValueUtils';
import { IOwnedProduct } from '../../models/OwnedProduct';
import { IProductDescription } from '../../../ts/models/product/IProductDescription';
import DOMUtils from '../../../ts/utils/DOMUtils';
import Loading, { LoadingSize } from '../../../ts/components/common/Loading';

type ILibraryProduct = IOwnedProduct | IProductDescription;

export interface IProps { 
    store: ManningOnlineStore; 
    defaultProductsFilter: "all" | "books" | "videos" | "liveprojects" | "purchased" | "meap" | "audio" | "series";
    defaultSortFilter: "last read" | "date bought" | "date updated" | "popular";

    mimicDashboard: boolean;
}
export interface IState {
    productsFilter: "all" | "books" | "videos" | "liveprojects" | "purchased" | "meap" | "audio" | "series";
    categoriesFilter: string;
    sort: "last read" | "date bought" | "date updated" | "popular";

    showAll: boolean;
    selectedMissingBookSelect: string;
}
export class ManningOnlineAllProducts extends React.Component <IProps, IState> {
    private hasBeenMounted: boolean;
    private allProductsRef: HTMLElement;
    private root: HTMLElement;
    
    constructor(props: IProps) {
        super(props);

        this.onStoreChange = this.onStoreChange.bind(this);
        this.handleReadingListClick = this.handleReadingListClick.bind(this);
        this.handleSearch = this.handleSearch.bind(this);

        this.handleProductsFilterChange = this.handleProductsFilterChange.bind(this);
        this.handleSortChange = this.handleSortChange.bind(this);
        this.handleCategoriesFilterChange = this.handleCategoriesFilterChange.bind(this);

        this.handleAllProductsRef = this.handleAllProductsRef.bind(this);
        this.handleAllProductsAnchorVisible = this.handleAllProductsAnchorVisible.bind(this);
        this.handleSearchInputChange = this.handleSearchInputChange.bind(this);
        this.onRef = this.onRef.bind(this);
        this.onSearchKeyDown = this.onSearchKeyDown.bind(this);
        this.clearSearch = this.clearSearch.bind(this);

        this.onMissingBookSelect = this.onMissingBookSelect.bind(this);

        this.state = {
            productsFilter: props.defaultProductsFilter || "all",
            categoriesFilter: "all",
            sort: props.defaultSortFilter || "last read",
            showAll: false,
            selectedMissingBookSelect: ""
        };
    }

    public UNSAFE_componentWillReceiveProps(nextProps: IProps){
        this.setState({
            productsFilter: this.state.productsFilter || nextProps.defaultProductsFilter || "all",
            sort: this.state.sort || nextProps.defaultSortFilter || "last read",
            categoriesFilter: this.state.categoriesFilter || "all"
        });
    }

    private onStoreChange(){
        this.hasBeenMounted && this.forceUpdate();
    }

    public componentDidMount(){
        this.hasBeenMounted = true;
        this.props.store.onCatalogChange(this.onStoreChange);

        const searchInput = this.getSearchInput();
        if(searchInput != null){
            searchInput.addEventListener("change", this.handleSearchInputChange);
            searchInput.addEventListener("keydown", this.onSearchKeyDown);
            searchInput.value = "";
        }

        const searchButton = this.getSearchButton();
        if(searchButton != null){
            searchButton.addEventListener("click", this.handleSearch);
        }

        const searchClose = this.getSearchCloseButton();
        if(searchClose != null){
            searchClose.addEventListener("click", this.clearSearch);
            searchClose.style.display = "none";
            searchClose.style.visibility = "hidden";
        }
    }

    public componentWillUnmount(){
        this.hasBeenMounted = false;
        
        this.allProductsRef = null;

        this.props.store.offCatalogChange(this.onStoreChange);

        const searchInput = this.getSearchInput();
        if(searchInput != null){
            searchInput.removeEventListener("change", this.handleSearchInputChange);
            searchInput.removeEventListener("keydown", this.onSearchKeyDown);
        }

        const searchButton = this.getSearchButton();
        if(searchButton != null){
            searchButton.removeEventListener("click", this.handleSearch);
        }

        const searchClose = this.getSearchCloseButton();
        if(searchClose != null){
            searchClose.removeEventListener("click", this.clearSearch);
        }
    }

    private onRef(root: HTMLElement){
        this.root = root;
    }

    public render(){
        const displayedProducts = this.getDisplayedProducts();

        const containsDummyProducts = displayedProducts.find(it => it.productType == "dummy") != null;

        return <div className="manning-online-all-products-root" ref={this.onRef}>
                    { this.renderProducts(displayedProducts, containsDummyProducts)}
               </div>
    }

    private renderProducts(allProducts: ILibraryProduct[], containsDummyProducts: boolean){
        if((allProducts == null || allProducts.length == 0) 
        && this.getSearchQuery() != ""){
            this.handleAllProductsRef(null);
            return <div className="manning-online-all-products-section">
                        <div className="manning-online-all-products-section-header">
                            <div className="manning-online-all-products-section-header-top">
                                <strong>all titles: </strong> 
                            </div>
                        </div>
                        <div className="manning-online-all-products-products-wrapper">
                            No titles match the target query
                        </div>
                   </div>
        }

        let displayedProducts = allProducts.concat([]);

        if(!this.state.showAll){ displayedProducts = displayedProducts.slice(0, 20); }
        
        return <div className="manning-online-all-products-section" ref={this.handleAllProductsRef}>
                    <div className={classnames("manning-online-all-products-section-header", { "_mimick-dashboard-header": this.props.mimicDashboard})}>
                        <div className="manning-online-all-products-section-header-top">
                            <span className='_label _products-header-label'>products: </span> 
                            <select className={`_select-${this.state.productsFilter} _products-header-select`} value={this.state.productsFilter} onChange={this.handleProductsFilterChange}>
                                <option value="all">all</option>
                                <option value="books">books</option>
                                <option value="videos">videos</option>
                                <option value="liveprojects">liveprojects</option>
                                <option value="series">liveproject series</option>
                                <option value="audio">audio</option>
                                <option value="purchased">purchased</option>
                                <option value="meap">MEAP</option>
                            </select>
                            <span className='_label'>subject:</span>
                            <select className={`_select-${this.state.productsFilter}`} value={this.state.categoriesFilter} onChange={this.handleCategoriesFilterChange} style={{"maxWidth": this.state.categoriesFilter == "all" ? "48px" : `${24 + this.state.categoriesFilter.length * 10}px`}}>
                                {this.renderCategoriesFilterOptions()}
                            </select>
                            <span className='_label'>sort: </span>
                            <select value={this.state.sort} onChange={this.handleSortChange} style={{"maxWidth": `${24 + this.state.sort.length * 12}px`}}>
                                <option value="last read">last read</option>
                                <option value="date bought">date bought</option>
                                <option value="date updated">date updated</option>
                                <option value="popular">popular</option>
                            </select>
                            { this.props.mimicDashboard ? this.renderDashboardButtons() : null}
                        </div>
                    </div>
                    <div className="manning-online-all-products-products-wrapper">
                        { displayedProducts.map(product => {
                            if(this.isOwnedProduct(product)){
                                return this.renderOwnedProduct(product as IOwnedProduct);
                            }
                            else {
                                return this.renderProduct(product);
                            }
                        })}
                        { displayedProducts.length == 0 ? "No products to display" : null}
                        <div className="_visibility-anchor">
                            {allProducts.length != displayedProducts.length ? <Loading loadingSize={LoadingSize.Medium}></Loading> : null }
                        </div>
                    </div>
                </div>
    }

    private renderDashboardButtons(){
        return <div className="_dashboard-buttons-container">
                    <a className='_wishlist-button' target='_blank' href={this.getMarketplaceLink("wishList/wishLists")} title="wish list"><i className="fa fa-heart"></i> <span>wish list</span></a>
                    <a className='_account-details-button' target='_blank' href={this.getMarketplaceLink("dashboard/details")} title="account details"><i className="fa fa-cog"></i> <span>account details</span></a>
                    <a className='_register-bundle-button' target='_blank' href={this.getMarketplaceLink("dashboard/accessCodeRegistration")} title="register bundle"><i className="fa fa-plus-square"></i> <span>register bundle</span></a>
                    <a className='_register-book-button' target='_blank' href={this.getMarketplaceLink("dashboard/bookRegistrationLanding")} title="register book"><i className="fa fa-plus"></i> <span>register book</span></a>
                    <select className='_missing-book-select' value={this.state.selectedMissingBookSelect} onSelect={this.onMissingBookSelect} onChange={this.onMissingBookSelect}>
                        <option value="" disabled>missing a book?</option>
                        <option>add email address to your account</option>
                        <option>contact support</option>
                    </select>
               </div>
    }

    private onMissingBookSelect(e){
        if(e.target.value == 'add email address to your account'){
            window.open(this.getMarketplaceLink("dashboard/details?template=changeEmailPassword"), "_blank");
        }
        else {
            window.open("mailto:support@manning.com", "_blank");
        }

        e.preventDefault();
        return false;
    }

    private getMarketplaceLink(relativeUrl: string): string {
        return this.props.store.provider.config.client.marketplace + "/" + relativeUrl;
    }

    private renderCategoriesFilterOptions(){
        const options = [<option key="all" value="all">all</option>];
        const tagCounterMap: {[tag: string]: number} = {};

        let allProducts = this.getSelectedProductTypeProducts();
        for(const it of allProducts){ 
            if(it.tags){
                for(const tag of it.tags){
                    if(!tagCounterMap[tag]) { tagCounterMap[tag] = 0; }
                    tagCounterMap[tag]++;
                }
            }
        }

        const tags: {tag: string, count: number}[] = [];
        for(const tag in tagCounterMap){ tags.push({tag: tag, count: tagCounterMap[tag]}); }

        //tags.sort((a, b) => b.count - a.count); 
        try {
            tags.sort((a, b) => a.tag.toLowerCase().localeCompare(b.tag.toLowerCase())); 
        }
        catch(e){ console.log(e); }
        

        for(const tag of tags){
            options.push(<option key={tag.tag}>{tag.tag}</option>);
        }

        return options;
    }

    private renderOwnedProduct(product: IOwnedProduct){
        const isDummy = product.productType == "dummy";
            
        return <ProductListItem key={product.id} 
                                authors={product.authors}
                                id={product.id}
                                title={product.title}
                                link={product.link}
                                licenses={product.licenses}
                                date={ValueUtils.getDateAsString(product.licenseDate)}
                                codeDownloadLink={product.codeDownloadLink || this.getFallbackCodeUrl(product)}
                                coverPath={product.coverPath}
                                forumUrl={product.forumUrl || this.getFallbackForumUrl(product)}
                                isMeap={product.isMeap}
                                isLiveprojectSeries={product.productType == "series"}
                                isLiveproject={product.productType == "project"}
                                isLivevideo={product.productType == "video"}
                                isLivebook={product.productType == "book"}
                                coverPlaceholder={this.props.store.coverPlaceholder}
                                readingListButtonState={this.props.store.isInAtLeastOneReadingList(product) ? ReadingListButtonState.Edit : ReadingListButtonState.Add}
                                onReadingListClick={isDummy ? null : this.handleReadingListClick}
                                data={`${product.id}`}
                                isOwnedByUser={!isDummy}
                                showAddToReadingListLink={!isDummy}
                                onDragStart={this.props.store.catalogState.coversAreDraggable ? this.props.store.onProductCoverDragStart : null}/>
    }

    private getFallbackCodeUrl(product: IOwnedProduct | IProductDescription): string {
        if(!product) { return ""; }

        if(product.productType == "book"){
            if((product as IProductDescription).marketplaceLink){
                return (product as IProductDescription).marketplaceLink
            }
            
            return (product as IOwnedProduct).link || ""
        }

        return "";
    }

    private getFallbackForumUrl(product: IOwnedProduct | IProductDescription): string {
        if(!product) { return "";}
        
        if((product.productType == "book"
        || product.productType == "video")){
            if((product as IProductDescription).externalId){
                return `https://livebook.manning.com/forum?product=${(product as IProductDescription).externalId}&p=1`;
            }
        }

        return "";
    }

    private renderProduct(product: IProductDescription){
        const isDummy = product.productType == "dummy";
                            
        return <ProductListItem key={product.manningId + product.title}
                                authors={product.authors}
                                coverPath={product.coverPath}
                                coverPlaceholder={this.props.store.coverPlaceholder}
                                title={product.title}
                                link={product.contentLink || product.link}
                                id={product.manningId}
                                codeDownloadLink={this.getFallbackCodeUrl(product)}
                                forumUrl={this.getFallbackForumUrl(product)}
                                data={`${product.manningId}`}
                                isMeap={product.isMeap}
                                isNew={product.productType != "dummy" && ValueUtils.isWithinLastTwoWeeks(product.dateObj)}
                                isLivevideo={product.productType == "video"}
                                isLiveproject={product.productType == "project"}
                                isLivebook={product.productType == "book"}
                                date = {isDummy ? "" : product.date}
                                readingListButtonState={this.props.store.isInAtLeastOneReadingList(product) ? ReadingListButtonState.Edit : ReadingListButtonState.Add}
                                showAddToReadingListLink={!isDummy}
                                onReadingListClick={isDummy ? null : this.handleReadingListClick}
                                onDragStart={this.props.store.catalogState.coversAreDraggable ? this.props.store.onProductCoverDragStart : null}/>
    }
    
    private handleAllProductsRef(allProductsRef: HTMLElement){
        if(this.allProductsRef == null && allProductsRef != null){
            const visibilityAnchor = allProductsRef.querySelector("._visibility-anchor");
            
            if(visibilityAnchor != null){
                DOMUtils.inViewport(visibilityAnchor, this.handleAllProductsAnchorVisible);
            }
        }

        this.allProductsRef = allProductsRef;
    }

    private handleAllProductsAnchorVisible(){
        if(!this.state.showAll){
            this.setState({
                showAll: true
            });
        }
    }

    private handleProductsFilterChange(e){
        this.setState({
            productsFilter: e.target.value,
            categoriesFilter: this.state.categoriesFilter
        });
    }

    private handleSortChange(e){
        this.setState({
            sort: e.target.value,
        });
    }

    private handleCategoriesFilterChange(e){
        this.setState({
            categoriesFilter: e.target.value,
        });
    }
    
    private handleReadingListClick(productId) {
        const product = this.props.store.catalogState.allProducts.find(it => it.manningId == productId);
        if(product == null) { return; }
        
        this.props.store.requestReadingListSpecificationForProduct(product);
    }

    private handleSearch(e){
        this.setState({
            showAll: true
        });
    }

    private handleSearchInputChange(e){
        const query = e.target.value;
        const closeButton = this.getSearchCloseButton();
        if(closeButton != null){
            closeButton.style.display = query ? "flex" : "none";
            closeButton.style.visibility = query ? "visible" : "hidden"; 
        }

        if(!query){
            setTimeout(() => this.setState({}), 16);
        }
    }

    private onSearchKeyDown(e){
        const keyCode = e.keyCode || e.which;
        if(keyCode == 13) {
            setTimeout(() => this.setState({}), 16);
        }
        else if(keyCode == 27){
            this.clearSearch();
        }
    }

    private clearSearch(){
        if(this.root == null) { return; }
        
        const searchInput = this.getSearchInput();
        if(searchInput != null){
            searchInput.value = "";
        }

        const closeButton = this.getSearchCloseButton();
        if(closeButton != null){
            closeButton.style.display = "none";
            closeButton.style.visibility = "hidden;" 
        }
        
        setTimeout(() => this.setState({}), 16);
    }

    private getSearchQuery(){
        if(this.root == null) { return ""; }

        const searchInput = this.getSearchInput();
        return searchInput != null ? searchInput.value : "";
    }

    private getSelectedProductTypeProducts(): ILibraryProduct[] {
        let result = this.props.store.getDisplayedAllProducts();
        
        let allProducts = result.allProducts;
        let ownedProducts = result.ownedProducts.filter(it => !it.isDummy);
        let libraryProducts: ILibraryProduct[] = [...ownedProducts, ...allProducts];

        if(this.state.productsFilter == "books"){
            return libraryProducts.filter(it => it.productType == "book");
        }
        else if(this.state.productsFilter == "videos"){
            return libraryProducts.filter(it => it.productType == "video");
        }
        else if(this.state.productsFilter == "liveprojects"){
            return libraryProducts.filter(it => it.productType == "project");
        }
        else if(this.state.productsFilter == "series"){
            return libraryProducts.filter(it => it.productType == "series");
        }
        else if(this.state.productsFilter == "audio"){
            return libraryProducts.filter(it => it.isLiveaudio);
        }
        else if(this.state.productsFilter == "meap"){
            return libraryProducts.filter(it => it.isMeap);
        }
        else if(this.state.productsFilter == "purchased"){
            return ownedProducts;
        }
        else {
            return libraryProducts;
        }
    }

    private getDisplayedProducts(): ILibraryProduct[] {
        let result = this.props.store.getDisplayedAllProducts();
        
        let allProducts = result.allProducts;
        let dummyAllProducts = (allProducts || []).filter(it => it.isDummy);

        let ownedProducts = dummyAllProducts.length == allProducts.length ? result.ownedProducts : result.ownedProducts.filter(it => !it.isDummy);
        let libraryProducts: ILibraryProduct[] = [...ownedProducts, ...allProducts];

        if(this.state.productsFilter == "books"){
            libraryProducts = libraryProducts.filter(it => it.productType == "book");
        }
        else if(this.state.productsFilter == "videos"){
            libraryProducts = libraryProducts.filter(it => it.productType == "video");
        }
        else if(this.state.productsFilter == "liveprojects"){
            libraryProducts = libraryProducts.filter(it => it.productType == "project");
        }
        else if(this.state.productsFilter == "series"){
            libraryProducts = libraryProducts.filter(it => it.productType == "series");
        }
        else if(this.state.productsFilter == "audio"){
            libraryProducts = libraryProducts.filter(it => it.isLiveaudio);
        }
        else if(this.state.productsFilter == "meap"){
            libraryProducts = libraryProducts.filter(it => it.isMeap);
        }
        else if(this.state.productsFilter == "purchased"){
            libraryProducts = ownedProducts;
        }

        if(this.state.categoriesFilter != "all"){
            libraryProducts = libraryProducts.filter(it => it.tags && it.tags.indexOf(this.state.categoriesFilter) >= 0);
        }

        if(this.getSearchQuery().trim()){
            const query = this.getSearchQuery().toLowerCase();
            libraryProducts = libraryProducts.filter(it => this.filterByQuery(it, query));
        }

        if(this.state.sort == "last read"){
            this.sortByLastRead(libraryProducts);
        }
        else if(this.state.sort == "date bought"){
            this.sortByDateBought(libraryProducts);
        }
        else if(this.state.sort == "date updated"){
            this.sortByDateUpdated(libraryProducts);
        }
        else if(this.state.sort == "popular"){
            this.sortByPopular(libraryProducts);
        }

        return libraryProducts;
    }

    private sortByLastRead(libraryProducts: ILibraryProduct[]){
        this.sortByDateBought(libraryProducts);
        
        const recentReading = this.props.store.catalogState.recentReading;
        if(recentReading == null || recentReading.length == 0 || recentReading.filter(it => it.isDummy).length > 0){
            return;
        }

        const recentReadingIdMap = {};
        for(const item of recentReading){ recentReadingIdMap[item.manningId] = true;}
        const recentReadingIdIndexMap = {};
        for(let i = 0; i < recentReading.length; i++){
            recentReadingIdIndexMap[recentReading[i].manningId] = i;
        }

        const itemsToRemove = libraryProducts.filter(it => recentReadingIdMap[(it as any).manningId || it.id]);
        for(const it of itemsToRemove){ ValueUtils.removeItemFromArray(libraryProducts, it); }

        itemsToRemove.sort((a, b) => {
            return recentReadingIdIndexMap[(b as any).manningId || b.id] - recentReadingIdIndexMap[(a as any).manningId || a.id];
        });

        for(const item of itemsToRemove){ libraryProducts.unshift(item); }
    }

    private sortByDateBought(libraryProducts: ILibraryProduct[]){
        const productSalesInfoMap = this.props.store.catalogState.productSalesInfoMap;
        
        libraryProducts.sort((a:IOwnedProduct, b:IOwnedProduct) => {
            if(a.licenseDate && b.licenseDate){ return b.licenseDate.getTime() - a.licenseDate.getTime(); }
            if(!a.licenseDate && b.licenseDate){ return 1; }
            else if(a.licenseDate && !b.licenseDate){ return -1; }

            if(a.isDummy && !b.isDummy){ return 1; }
            else if(!a.isDummy && b.isDummy){ return -1; }
            else if(a.isDummy && b.isDummy) { return 0; }

            return (productSalesInfoMap[(b as any).manningId || b.id] || 0) - (productSalesInfoMap[(a as any).manningId || a.id] || 0)
        });
    }

    private sortByDateUpdated(libraryProducts: ILibraryProduct[]){
        libraryProducts.sort((a, b) => {
            if(a.isDummy && !b.isDummy){ return 1; }
            else if(!a.isDummy && b.isDummy){ return -1; }
            else if(a.isDummy && b.isDummy) { return 0; }

            const aDate = (a as IProductDescription).dateObj || (a as IOwnedProduct).productLastSignificantDate;
            const bDate = (b as IProductDescription).dateObj || (b as IOwnedProduct).productLastSignificantDate;
            
            return bDate.getTime() - aDate.getTime();
        });
    }

    private sortByPopular(libraryProducts: ILibraryProduct[]){
        const productSalesInfoMap = this.props.store.catalogState.productSalesInfoMap;
        libraryProducts.sort((a, b) => {
            return (productSalesInfoMap[(b as IProductDescription).manningId || b.id] || 0) - (productSalesInfoMap[(a as IProductDescription).manningId || a.id] || 0);
        });
    }

    private isOwnedProduct(item: ILibraryProduct): boolean {
        return (item as IOwnedProduct).licenseDate != null;
    }

    private filterByQuery(item: {title?: string, authors?: string}, query: string): boolean {
        let title = (item.title || "");
        let authors = (item.authors || "");
        
        if(title.toLowerCase().indexOf(query) >= 0
        || authors.toLowerCase().indexOf(query) >= 0) {
            return true;
        }

        title = title.replace(/[^a-zA-Z]/g, "");
        authors = authors.replace(/[^a-zA-Z]/g, "");

        query = query.replace(/[^a-zA-Z]/g, "");

        if(title.toLowerCase().indexOf(query) >= 0
        || authors.toLowerCase().indexOf(query) >= 0) {
            return true;
        }

        return false;
    }

    private getSearchInput(): HTMLInputElement {
        return document.querySelector("#search-additional-input") as HTMLInputElement;
    }

    private getSearchButton(): HTMLButtonElement {
        return document.querySelector("#search-additional-button") as HTMLButtonElement;
    }

    private getSearchCloseButton(): HTMLButtonElement {
        return document.querySelector("#search-additional-close") as HTMLButtonElement
    }
}